import clsx from 'clsx';
import ApplicationSummary from 'components/Applications/ApplicationSummary';
import MutedText from 'components/common/MutedText';
import EvaluationIcons from 'components/HubertUI/EvaluationStatus/EvaluationIcons';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertDialog from 'components/HubertUI/HubertDialog';
import HubertSelect from 'components/HubertUI/HubertSelect';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { EEvaluationType } from 'types/evaluation.types';
import { ScoreLimits } from 'types/scores.types';
import { isDeveloper } from 'types/user.types';
import type { ScoreInterviewContextType } from '../scoring.context';
import { useScoreInterview } from '../scoring.context';

const ScoreInterviewActionBar = () => {
  const {
    user,
    languageList,
    preferredLanguage,
    setPreferredLanguage,
    application,

    // Interview summary - existing and new
    summary,
    existingInterviewSummary,

    scoreTable,
    setScoreTable,
    scoreTableDetails,

    saveScores,
    isManuallyScoreInterviewByIdRequestLoading,

    updateIntegration,
    setUpdateIntegration,

    reScore,
    isReScoreInterviewByIdRequestLoading,
  } = useScoreInterview() as ScoreInterviewContextType;
  const [bonusPoints, setBonusPoints] = useState<number>(0);
  const [weightedPoints, setWeightedPoints] = useState<number>(0);
  const [totalScore, setTotalScore] = useState<number>(0);

  useEffect(() => {
    let bonusTemp = 0;
    let weightedTemp = 0;
    scoreTableDetails.map((detail) => {
      if (detail.type === 'bonus') {
        bonusTemp += detail.score ?? 0;
      } else if (detail.type === 'weighted') {
        weightedTemp += detail.score ?? 0;
      } else {
        toast.error('ScoreInterviewActionBar - erroneous detail type.');
      }
    });

    setBonusPoints(bonusTemp);
    setWeightedPoints(weightedTemp);
    setTotalScore(bonusTemp + weightedTemp);
  }, [scoreTableDetails]);

  useEffect(() => {
    if (scoreTable) {
      setScoreTable({
        ...scoreTable,
        bonuspoints_sum: bonusPoints,
        weighted_total: weightedPoints,
        total_score: totalScore,
      });
    }
  }, [bonusPoints, weightedPoints, totalScore]);

  if (!scoreTable) {
    return null;
  }

  return (
    <div
      className={clsx(
        'min-h-[calc(100vh-64px)]',
        'flex flex-col justify-between gap-4',
        'bg-gray-100',
        'py-12',
        'px-1',
        'text-sm',
      )}
    >
      <div className="flex flex-col justify-between gap-4">
        <HubertSelect
          valuePlaceholder="Scoring language"
          onValueChange={(e) => {
            setPreferredLanguage(e);
            if (e === 'en') {
              toast.info(
                'Switching to English will try to display all questions in English (en-gb). Please ensure every question and its translations are available in English (en-gb).',
              );
            }
          }}
          value={preferredLanguage ?? 'original'}
          list={languageList ?? []}
        />
      </div>

      <div className="flex flex-col items-center justify-between gap-2">
        <MutedText text="Threshold" className="font-medium text-gray-500" />
        <span>{scoreTable.threshold}</span>
      </div>

      <div className="flex flex-col items-center justify-between gap-2">
        <MutedText text="Qualified" className="font-medium text-gray-500" />
        {scoreTable.autoQualified === true ? (
          <HubertBadge variant={'success'} className="m-auto">
            Auto Qualified
          </HubertBadge>
        ) : null}
        {scoreTable.qualified ? (
          <EvaluationIcons evaluation={{ name: EEvaluationType.Qualified }} />
        ) : (
          <EvaluationIcons evaluation={{ name: EEvaluationType.Disqualified }} />
        )}
      </div>

      <div className="flex flex-col items-center justify-between gap-2">
        <MutedText text="Bonus points" className="font-medium text-gray-500" />
        <span className="hubert-vertical-center text-base">
          <EvaluationIcons
            evaluation={{
              name: EEvaluationType.Bonus,
              bonus_points: Math.round(bonusPoints * 10) / 10,
            }}
          />
          <span className="pl-4 text-base text-gray-400">{`/ ${Math.round(scoreTable.bonuspoints_max * 10) / 10}`}</span>
        </span>
      </div>

      <div className="flex flex-col items-center justify-between gap-2">
        <MutedText text="Base points" className="font-medium text-gray-500" />
        <span className="hubert-vertical-center text-base">
          <EvaluationIcons
            threshold={scoreTable.threshold}
            evaluation={{
              name: EEvaluationType.Point,
              points: Math.round(scoreTable.weighted_total * 10) / 10,
            }}
          />
          <span className="pl-2 text-base text-gray-400">{`/ ${Math.round(scoreTable.weighted_max * 10) / 10}`}</span>
        </span>
      </div>

      <div className="flex flex-col items-center justify-between gap-2">
        <MutedText text="Total score" className="font-medium text-gray-500" />
        <span className="hubert-vertical-center text-base">
          <EvaluationIcons
            threshold={scoreTable.threshold}
            evaluation={{
              name: EEvaluationType.Point,
              points: Math.round(scoreTable.total_score * 10) / 10,
            }}
          />
          <span className="pl-2 text-base text-gray-400">{`/ ${ScoreLimits.Max}`}</span>
        </span>
      </div>

      <div className="flex flex-col justify-between gap-4">
        {existingInterviewSummary ? (
          <HubertDialog>
            <HubertDialog.Trigger asChild>
              <HubertBadge variant={'warning'} className="m-auto cursor-pointer">
                Existing summary
              </HubertBadge>
            </HubertDialog.Trigger>
            <HubertDialog.Content position="center" portalContent={false}>
              <div className="p-2">
                {/* @ts-ignore */}
                <ApplicationSummary applicationId={application._id} summary={existingInterviewSummary} />
              </div>
            </HubertDialog.Content>
          </HubertDialog>
        ) : null}

        <HubertDialog>
          <HubertDialog.Trigger asChild>
            <HubertBadge variant={summary ? 'success' : 'danger'} className="m-auto cursor-pointer">
              Preview summary
            </HubertBadge>
          </HubertDialog.Trigger>
          <HubertDialog.Content position="center" portalContent={false}>
            <div className="p-2">
              {/* @ts-ignore */}
              <ApplicationSummary applicationId={application._id} summary={summary} />
            </div>
          </HubertDialog.Content>
        </HubertDialog>

        <div className="flex flex-col justify-between gap-2">
          <MutedText text="Update integration" className="text-center font-medium text-gray-500" />
          <div className="text-center text-base">
            <HubertSwitch
              checked={updateIntegration}
              onCheckedChange={(x) => {
                console.log('setUpdateIntegration', x);
                setUpdateIntegration(x);
              }}
            />
          </div>
        </div>

        <HubertButton
          variant={'outline'}
          onClick={() => {
            saveScores();
          }}
          loading={isManuallyScoreInterviewByIdRequestLoading}
        >
          Save
        </HubertButton>

        <HubertButton
          className={clsx(!isDeveloper(user) && 'hidden')}
          variant={'outline'}
          disabled={isReScoreInterviewByIdRequestLoading}
          onClick={() => {
            reScore();
          }}
          loading={isReScoreInterviewByIdRequestLoading}
        >
          Full rescore
        </HubertButton>
      </div>
    </div>
  );
};

export default ScoreInterviewActionBar;
